import { TranslateService } from '@ngx-translate/core';
import { DeviceAddModalComponent } from '../modules/device/modals/device-add-modal/device-add-modal.component';
import { environment } from './../../../../environments/environment';
import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { NbDialogService } from '@nebular/theme';
import { ReportProblemModalComponent } from '../../core/modals/report-problem-modal/report-problem-modal.component';
import { resourceTypes } from '../constants/resourceTypes';
import { NewVersionAvailableComponent } from '../modules/account/modals/new-version-available/new-version-available.component';
import { PrivacyPolicyModalComponent } from '../modules/account/modals/privacy-policy-modal/privacy-policy-modal.component';
import { AddMoreResourcesModalComponent } from '../modules/account/modals/add-more-resources-modal/add-more-resources-modal.component';
import { UnavailableFeatureModalComponent } from '../modules/account/modals/unavailable-feature-modal/unavailable-feature-modal.component';
import { UserService } from '@shared/modules/account/services/user.service';
import { MultiprintComponent } from '../../multiprint/components/multiprint/multiprint.component';
import { Subscription } from 'rxjs';
import { TwoFactorConfigModalComponent } from '@core/modals/two-factor-config-modal/two-factor-config-modal.component';
import { AccessService } from '@access/access.service';
import { PERMISSIONS } from '@access/permissions';
import { SessionReplacedModalComponent } from '@core/modals/session-replaced-modal/session-replaced-modal.component';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private subscription = new Subscription();

  constructor(
    private api: ApiService,
    private dialogService: NbDialogService,
    private userService: UserService,
    private translateService: TranslateService,
    private readonly accessService: AccessService
  ) {}

  showNewVersionAvailableModal(newVersionDelivered: boolean): Promise<void> {
    return new Promise((resolve) => {
      this.api.getCloudNewVersionInformation(environment.appVersion).subscribe({
        error: () => resolve(),
        next: (res) => {
          if (!newVersionDelivered || !res) {
            return resolve();
          }

          this.dialogService
            .open<any>(NewVersionAvailableComponent, {
              context: { features: res },
              closeOnBackdropClick: false,
              closeOnEsc: false,
            })
            .onClose.subscribe(() => resolve());
        },
      });
    });
  }

  showPrivacyPolicyModal(privacyPolicyDelivered: boolean): Promise<void> {
    return new Promise((resolve) => {
      if (privacyPolicyDelivered) {
        this.dialogService
          .open<any>(PrivacyPolicyModalComponent, {
            closeOnBackdropClick: false,
            closeOnEsc: false,
          })
          .onClose.subscribe(() => resolve());
      } else {
        resolve();
      }
    });
  }

  showMultiPrintModal(selectedDeviceIds: Array<string> = []) {
    if (!this.accessService.hasPermission(PERMISSIONS.Printing.Multi)) {
      this.showUnavailableMultiPrintModal();
      return;
    }

    this.subscription.add(
      this.api.getTransferResources().subscribe({
        error: (err) => {},
        next: (res) => {
          if (res.totalTransfer - res.currentTransfer > 0) {
            this.dialogService.open<any>(MultiprintComponent, {
              context: {
                transferResources: res,
                onlyDeviceIds: selectedDeviceIds,
              },
            });

            return;
          }

          this.dialogService.open<any>(AddMoreResourcesModalComponent, {
            context: {
              resource: resourceTypes.transfer,
              currentCount: res.currentTransfer,
              totalCount: res.totalTransfer,
              totalLimit: res.totalTransferLimit,
              priceForOneUnit: res.priceForOneUnit,
              title: 'bulk.bulk-print',
              currency: res.currency,
              icon: 'play-circle-outline',
              isActive: res.isActive,
            },
          });
        },
      })
    );
  }

  showUnavailableMultiPrintModal() {
    this.dialogService.open<any>(UnavailableFeatureModalComponent, {
      context: {
        title: this.translateService.instant('bulk.bulk-print'),
        image: 'assets/modals/unavailable-bulkprint.png',
        attention: `${this.translateService.instant(
          'packets.function-is-not-included-in-your-plan'
        )} ${this.translateService.instant('packets.professional')}.`,
        descriptionTitle: this.translateService.instant('bulk.bulk-print'),
        descriptionContent: this.translateService.instant(
          'packets.bulk-print-explanation'
        ),
      },
    });
  }

  showModalDeviceAdd() {
    this.subscription.add(
      this.api.getDevicesResources().subscribe({
        error: (err) => {},
        next: (res) => {
          if (res.totalDeviceCount - res.currentDeviceCount > 0) {
            this.dialogService.open<any>(DeviceAddModalComponent, {});
          } else {
            this.dialogService.open<any>(AddMoreResourcesModalComponent, {
              context: {
                resource: resourceTypes.devices,
                currentCount: res.currentDeviceCount,
                totalCount: res.totalDeviceCount,
                totalLimit: res.totalDevicesLimit,
                priceForOneUnit: res.priceForOneUnit,
                isActive: res.isActive,
                currency: res.currency,
                title: 'common.add-new-device',
                icon: 'plus-outline',
              },
            });
          }
        },
      })
    );
  }

  showReportProblemModal() {
    this.dialogService.open<any>(ReportProblemModalComponent, {});
  }

  showUnavailableTeamsModal() {
    this.dialogService.open<any>(UnavailableFeatureModalComponent, {
      context: {
        title: this.translateService.instant('user.teams'),
        image: 'assets/modals/unavailable-teamwork.png',
        attention: `${this.translateService.instant(
          'packets.function-is-not-included-in-your-plan'
        )} ${this.translateService.instant('packets.professional')}.`,
        descriptionTitle: this.translateService.instant('packets.work-as-team'),
        descriptionContent: this.translateService.instant(
          'packets.teams-explanation'
        ),
      },
    });
  }

  showUnavailableFindDeviceModal() {
    this.dialogService.open<any>(UnavailableFeatureModalComponent, {
      context: {
        title: this.translateService.instant('common.find-device'),
        image: 'assets/modals/findDevice.jpg',
        attention: `${this.translateService.instant(
          'packets.function-is-not-included-in-your-plan'
        )} ${this.translateService.instant('packets.standard')}.`,
        descriptionTitle: this.translateService.instant('common.find-device'),
        descriptionContent: this.translateService.instant(
          'device.find-device-description'
        ),
      },
    });
  }

  showUnavailableShelvesModal() {
    this.dialogService.open<any>(UnavailableFeatureModalComponent, {
      context: {
        title: this.translateService.instant('shelf.shelfs'),
        image: 'assets/modals/unavailable-shelves.png',
        attention: `${this.translateService.instant(
          'packets.function-is-not-included-in-your-plan'
        )} ${this.translateService.instant('packets.standard')}.`,
        descriptionTitle: this.translateService.instant('shelf.shelfs'),
        descriptionContent: this.translateService.instant(
          'packets.shelves-explanation'
        ),
      },
    });
  }

  showTwoFactorConfigurationModal() {
    this.dialogService.open<any>(TwoFactorConfigModalComponent, {});
  }

  showSessionReplacedModal() {
    this.dialogService.open<any>(SessionReplacedModalComponent, {});
  }
}
